import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, MarketingPlaceDto, MarketingPlaceDtoPaginatedResponse, GooglePlaceTypeDto, Zone } from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../../services/permission.service';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { filter, take, takeUntil, Subject } from 'rxjs';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

@Component({
  selector: 'app-marketing-leads',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, HasPermissionDirective],
  templateUrl: './marketing-leads.component.html',
  styleUrl: './marketing-leads.component.css'
})
export class MarketingLeadsComponent implements OnInit, OnDestroy {
  leadsResponse: MarketingPlaceDtoPaginatedResponse | null = null;
  selectedLead: MarketingPlaceDto | null = null;
  showDetailsPanel = false;
  showUpdateStatusModal = false;
  leadForStatusUpdate: MarketingPlaceDto | null = null;

  private leadsSubscription$ = new Subject<void>();

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount: number = 0;
  totalPages: number = 1;

  // Filter properties
  filters = {
    geoZoneId: '',
    placeType: '',
    status: ''
  };

  // Zones
  zones: Zone[] = [];
  loadingZones = false;

  // Place types
  placeTypes: GooglePlaceTypeDto[] = [];
  loadingPlaceTypes = false;

  // Group place types by category
  get groupedPlaceTypes(): { category: string; types: GooglePlaceTypeDto[] }[] {
    const grouped = new Map<string, GooglePlaceTypeDto[]>();

    this.placeTypes.forEach(type => {
      const category = type.category || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(type);
    });

    // Convert to array and sort by category name
    return Array.from(grouped.entries())
      .map(([category, types]) => ({ category, types }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  // Update status form
  updateStatusForm = {
    status: '',
    remark: ''
  };

  loading = false;
  errorMessage = '';
  successMessage = '';

  // Make Math available in template
  Math = Math;

  private destroy$ = new Subject<void>();

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    public permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading leads
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadZones();
        this.loadPlaceTypes();
        // Don't load leads automatically - geoZoneId is required
        this.errorMessage = 'Please select a Geo Zone to load leads.';
      });
  }

  loadZones(): void {
    this.loadingZones = true;
    this.externalDataService.getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones || [];
          this.loadingZones = false;
        },
        error: (error) => {
          console.error('Error loading zones:', error);
          this.loadingZones = false;
          this.errorMessage = 'Failed to load zones. Please try again.';
        }
      });
  }

  loadPlaceTypes(): void {
    this.loadingPlaceTypes = true;
    this.externalDataService.getPlaceTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (types) => {
          this.placeTypes = types || [];
          this.loadingPlaceTypes = false;
        },
        error: (error) => {
          console.error('Error loading place types:', error);
          this.loadingPlaceTypes = false;
          // Don't show error to user, just log it - place types are optional
        }
      });
  }

  ngOnDestroy(): void {
    this.leadsSubscription$.next();
    this.leadsSubscription$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLeads(): void {
    // Validate that geoZoneId is selected
    if (!this.filters.geoZoneId || !this.filters.geoZoneId.trim()) {
      this.errorMessage = 'Please select a Geo Zone to load leads.';
      this.loading = false;
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Convert page number to page index (page index is 0-based)
    const pageIndex = this.currentPage - 1;

    // Prepare filter parameters
    const geoZoneId = this.filters.geoZoneId.trim();
    const placeType = this.filters.placeType?.trim() || undefined;
    const status = this.filters.status?.trim() || '';

    // Cancel any previous subscription
    this.leadsSubscription$.next();
    this.leadsSubscription$.complete();
    this.leadsSubscription$ = new Subject<void>();

    this.externalDataService.getMarketingPlacesByGeoZone(
      geoZoneId,
      placeType,
      status || undefined,
      pageIndex,
      this.pageSize
    )
      .pipe(takeUntil(this.leadsSubscription$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.leadsResponse = response;
            this.totalCount = response.totalCount || 0;
            this.calculateTotalPages();
          } else {
            this.leadsResponse = null;
            this.totalCount = 0;
            this.totalPages = 1;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading marketing leads:', error);
          this.loading = false;
          this.leadsResponse = null;

          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view marketing leads.';
          } else if (error.status === 404) {
            this.errorMessage = 'Marketing leads not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to load marketing leads. Please try again.';
          }
        }
      });
  }

  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onPageChange(page: number): void {
    if (!this.canLoadLeads()) {
      return;
    }
    this.currentPage = page;
    this.loadLeads();
  }

  onPageSizeChange(size: number): void {
    if (!this.canLoadLeads()) {
      return;
    }
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when changing page size
    this.loadLeads();
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when applying filters
    this.loadLeads();
  }

  clearFilters(): void {
    this.filters = {
      geoZoneId: '',
      placeType: '',
      status: ''
    };
    this.currentPage = 1; // Reset to first page when clearing filters
    // Don't load leads when clearing - geoZoneId is required
    this.errorMessage = 'Please select a Geo Zone to load leads.';
    // Cancel any pending subscription
    this.leadsSubscription$.next();
    this.leadsSubscription$.complete();
    this.leadsSubscription$ = new Subject<void>();
    this.leadsResponse = null;
    this.totalCount = 0;
    this.totalPages = 1;
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.geoZoneId.trim() ||
      this.filters.placeType.trim() ||
      this.filters.status.trim());
  }

  canLoadLeads(): boolean {
    return !!(this.filters.geoZoneId && this.filters.geoZoneId.trim());
  }

  viewLeadDetails(lead: MarketingPlaceDto): void {
    this.loading = true;
    this.errorMessage = '';
    this.externalDataService.getMarketingPlaceById(lead.placeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detailedLead) => {
          this.selectedLead = detailedLead;
          this.showDetailsPanel = true;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading lead details:', error);
          this.loading = false;
          this.errorMessage = 'Failed to load lead details. Please try again.';
        }
      });
  }

  closeDetailsPanel(): void {
    this.showDetailsPanel = false;
    this.selectedLead = null;
  }

  openUpdateStatusModal(lead: MarketingPlaceDto): void {
    this.leadForStatusUpdate = lead;
    this.updateStatusForm = {
      status: lead.status || '',
      remark: ''
    };
    this.showUpdateStatusModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeUpdateStatusModal(): void {
    this.showUpdateStatusModal = false;
    this.leadForStatusUpdate = null;
    this.updateStatusForm = {
      status: '',
      remark: ''
    };
  }

  updateLeadStatus(): void {
    if (!this.leadForStatusUpdate || !this.updateStatusForm.status.trim()) {
      this.errorMessage = 'Status is required.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request = {
      status: this.updateStatusForm.status.trim(),
      remark: this.updateStatusForm.remark.trim() || undefined
    };

    this.externalDataService.updateMarketingLeadStatus(
      this.leadForStatusUpdate.placeId,
      request
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedLead) => {
          this.loading = false;
          this.successMessage = 'Lead status updated successfully.';
          this.closeUpdateStatusModal();
          // Refresh the leads list
          this.loadLeads();
          // Update selected lead if it's the same one
          if (this.selectedLead && this.selectedLead.placeId === updatedLead.placeId) {
            this.selectedLead = updatedLead;
          }
        },
        error: (error) => {
          console.error('Error updating lead status:', error);
          this.loading = false;

          if (error.status === 400) {
            this.errorMessage = error.error?.detail || 'Invalid data provided. Please check your input.';
          } else if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to update lead status.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to update lead status. Please try again.';
          }
        }
      });
  }

  getStatusBadgeClass(status: string): string {
    const statusLower = status.toLowerCase();
    if (statusLower === 'converted') {
      return 'status-badge status-active';
    } else if (statusLower === 'pending') {
      return 'status-badge status-pending';
    } else if (statusLower === 'rejected' || statusLower === 'invalid') {
      return 'status-badge status-rejected';
    } else if (statusLower === 'followup') {
      return 'status-badge status-contacted';
    }
    return 'status-badge status-default';
  }
}

